import React, { useState, useEffect, useRef } from 'react';
import { 
  ShieldCheck, Search, Zap, Clock, BarChart3, 
  UserCheck, Lock, FileText, Activity, Target 
} from 'lucide-react';

const MDRBenefits = () => {
  const [activeBenefit, setActiveBenefit] = useState(benefitsData[0]);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const leftBenefits = benefitsData.slice(0, 5);
  const rightBenefits = benefitsData.slice(5, 10);

  return (
    <div ref={sectionRef} className="w-full py-12 lg:py-24 font-sans overflow-hidden text-slate-300">
      <div className="max-w-7xl mx-auto px-4 lg:px-1">
        
        {/* Responsive Grid System */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          
          

          {/* 2. Left Side Benefits */}
          <div className={`order-2 lg:order-none lg:col-span-3 flex flex-col gap-4 lg:gap-5 transition-all duration-1000 delay-300 transform ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10 lg:-translate-x-20'}`}>
            {leftBenefits.map((item, idx) => (
              <BenefitCard 
                key={idx} 
                item={item} 
                isActive={activeBenefit.title === item.title}
                // On mobile, force alignment to left. On desktop, align right.
                align="left" 
                desktopAlign="right"
                onHover={() => setActiveBenefit(item)} 
              />
            ))}
          </div>

          {/* 1. Central Illustration (Top on Mobile, Center on Desktop) */}
          <div className={`order-1 lg:order-none lg:col-span-3 flex justify-center relative py-6 lg:py-10 transition-all duration-1000 delay-100 transform ${isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-75'}`}>
            <div className="relative">
              <div className="absolute inset-0 rounded-full bg-emerald-500 animate-ping opacity-10"></div>
              <div className="absolute -inset-3 rounded-full border border-[#228B22]"></div>
              
              <div className="relative w-48 h-48 lg:w-64 lg:h-64 rounded-full bg-[#0a1212] shadow-[0_0_60px_rgba(16,185,129,0.15)] border border-[#00FF00] flex items-center justify-center">
                <div className="text-center px-4 lg:px-6">
                  <div className="bg-black text-[#32CD32] w-12 h-12 lg:w-16 lg:h-16 rounded-2xl flex items-center justify-center mx-auto mb-3 lg:mb-4 shadow-[0_0_20px_rgba(16,185,129,0.4)]">
                    <ShieldCheck size={28} className="lg:w-9 lg:h-9" strokeWidth={2.5} />
                  </div>
                  <p className="font-bold text-white text-lg lg:text-xl tracking-widest uppercase">Key Benefits</p>
                  <p className="text-[12px] lg:text-[15px] text-[#00FF00] uppercase tracking-[0.2em] lg:tracking-[0.3em] mt-1 font-mono">SecuWall MDR</p>
                </div>
              </div>
            </div>
          </div>

          {/* 3. Right Side Benefits */}
          <div className={`order-3 lg:order-none lg:col-span-3 flex flex-col gap-4 lg:gap-5 transition-all duration-1000 delay-300 transform ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10 lg:translate-x-20'}`}>
            {rightBenefits.map((item, idx) => (
              <BenefitCard 
                key={idx} 
                item={item} 
                isActive={activeBenefit.title === item.title}
                align="left" 
                onHover={() => setActiveBenefit(item)} 
              />
            ))}
          </div>

          {/* 4. Explanation Box */}
          <div className={`order-4 lg:order-none lg:col-span-3 transition-all duration-1000 delay-500 transform ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10 lg:translate-y-20'}`}>
            <div className="relative group h-full">
              <div className="absolute -inset-0.5 bg-emerald-500/20 rounded-3xl blur-xl group-hover:bg-emerald-500/30 transition duration-500"></div>
              <div className="relative bg-[#004526] p-6 lg:p-8 rounded-3xl border border-emerald-900/40 shadow-2xl min-h-[250px] lg:min-h-[350px] flex flex-col justify-center">
                <div className="w-12 h-12 lg:w-14 lg:h-14 bg-black text-emerald-500 rounded-xl flex items-center justify-center mb-4 lg:mb-6 border border-emerald-500/20">
                  {activeBenefit.icon}
                </div>
                <h3 className="text-xl lg:text-2xl font-bold text-white mb-3 lg:mb-4 tracking-tight">
                  {activeBenefit.title}
                </h3>
                <p className="text-white/90 leading-relaxed text-[15px] lg:text-[17px]" style={{ fontFamily: '"Times New Roman", Times, serif' }}>
                  {activeBenefit.description}
                </p>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

const BenefitCard = ({ item, align, desktopAlign, onHover, isActive }) => {
  // Logic to handle responsiveness in the card layout
  const alignmentClass = desktopAlign === 'right' 
    ? 'flex-row lg:flex-row-reverse text-left lg:text-right' 
    : 'flex-row text-left';

  return (
    <div 
      onMouseEnter={onHover}
      // Added onClick for mobile users who can't hover
      onClick={onHover}
      className={`group flex items-center gap-3 lg:gap-4 p-3 lg:p-4 rounded-xl border transition-all duration-500 cursor-pointer
        ${isActive 
          ? 'bg-[#009900] border-emerald-400 shadow-[0_0_25px_rgba(16,185,129,0.3)] scale-[1.02] lg:scale-105 z-10' 
          : 'bg-[#0d1515] border-emerald-900/30 hover:border-emerald-500/50 hover:bg-[#111c1c]'}
        ${alignmentClass}`}
    >
      <div className={`flex-shrink-0 w-9 h-9 lg:w-10 lg:h-10 rounded-lg flex items-center justify-center transition-all
        ${isActive ? 'bg-white text-emerald-600 shadow-inner' : 'bg-[#1a2b2b] text-emerald-500'}`}>
        {React.cloneElement(item.icon, { size: 18 })}
      </div>
      <span className={`text-[10px] lg:text-xs font-bold tracking-wider transition-colors uppercase
        ${isActive ? 'text-[#050a0a]' : 'text-slate-300 group-hover:text-emerald-400'}`}>
        {item.title}
      </span>
    </div>
  );
};

const benefitsData = [
  { title: "Proactive Threat Hunting", icon: <Search />, description: "Our experts don’t just wait for alerts—they actively hunt for hidden threats before attackers can cause damage." },
  { title: "Silence the Noise", icon: <Activity />, description: "We filter out the false positives so your team can focus on growth, not logs." },
  { title: "Stop Ransomware", icon: <Zap />, description: "Our average response time is measured in minutes, preventing lateral movement and data lockout." },
  { title: "Expert Support", icon: <UserCheck />, description: "Direct access to seasoned security analysts who act as an extension of your internal team." },
  { title: "Bridge the Talent Gap", icon: <Target />, description: "Gain an instant team of global security experts without the cost of a full-time SOC recruitment." },
  { title: "Scalable Protection", icon: <Clock />, description: "Grows automatically with your business—no new hires or expensive tool upgrades required." },
  { title: "Compliance Ready", icon: <FileText />, description: "Meeting regulatory requirements (GDPR, HIPAA, PCI) with detailed reporting and audit trails." },
  { title: "Data Analytics", icon: <BarChart3 />, description: "Using behavior analysis and machine learning to identify complex attack patterns." },
  { title: "Asset Discovery", icon: <Lock />, description: "Full visibility into all devices and software connected to your network to prevent shadow IT risks." },
  { title: "Cost Efficiency", icon: <ShieldCheck />, description: "Reduced overhead compared to building a 24/7 internal Security Operations Center (SOC)." },
];

export default MDRBenefits;